import { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useYandexMaps } from "@boundary/_hooks/useYandexMaps";
import { useMachineStore, useMapStore } from "@boundary/_hooks";
import { YandexMap } from "@boundary/components/yandexMap";
import { MachineMarker } from "@boundary/components/machineMarker";
import { UserLocationMarker } from "@boundary/components/userLocationMarker";
import { MachineListItem } from "@domain/dtos/models/machine.models";
import { MapService } from "@domain/services/map.service";
import styles from "./mapContainer.module.scss";

interface MapContainerProps {
  onMarkerClick?: (machineId: number) => void;
}

export function MapContainer({ onMarkerClick }: MapContainerProps) {
  const { Common } = useYandexMaps();
  const { items, selectedId } = useMachineStore();
  const { location, userLocation, isVisible } = useMapStore();
  const routerLocation = useLocation();
  
  // Проверяем путь напрямую для немедленного скрытия карты
  const showMap = routerLocation.pathname === "/map";
  // Используем прямое вычисление для немедленного скрытия, без ожидания store обновления
  const shouldBeVisible = showMap && isVisible;

  // Сохраняем инстанс карты (мемоизируем, чтобы не пересоздавать функцию)
  const handleMapReady = useCallback((mapInstance: any) => {
    MapService.setMap(mapInstance);
  }, []);

  // Мемоизируем ключ для сравнения items, чтобы избежать лишних пересозданий маркеров
  // ВАЖНО: Все хуки должны быть до раннего возврата!
  const itemsKey = useMemo(() => {
    return items.map(i => `${i.id}-${i.latitude}-${i.longitude}-${i.status}`).join(',');
  }, [items]);
  
  // Мемоизируем отфильтрованные items с координатами
  // Используем itemsKey для сравнения, но items нужен для фильтрации
  const machinesWithCoords = useMemo(() => {
    return items.filter(
      (item: MachineListItem) => item.latitude !== null && item.longitude !== null
    );
  }, [items, itemsKey]); // itemsKey для сравнения, items для фильтрации
  
  // Мемоизируем маркеры, чтобы не пересоздавать их на каждом рендере
  // Зависимости: Common, machinesWithCoords (мемоизированный массив), selectedId, onMarkerClick
  const markers = useMemo(() => {
    if (!Common) return [];
    
    return machinesWithCoords.map((item: MachineListItem) => {
      return (
        <Common.YMapMarker
          key={item.id}
          coordinates={[item.longitude!, item.latitude!]}
          onClick={() => onMarkerClick?.(item.id)}
        >
          <MachineMarker selected={item.id === selectedId} disabled={item.status === "offline"} />
        </Common.YMapMarker>
      );
    });
  }, [Common, machinesWithCoords, selectedId, onMarkerClick]);

  // Мемоизируем маркер пользователя
  const userMarker = useMemo(() => {
    if (!Common || !userLocation) return null;
    return (
      <Common.YMapMarker
        key={`user-location-${userLocation.latitude}-${userLocation.longitude}`}
        coordinates={[userLocation.longitude, userLocation.latitude]}
        zIndex={1000}
      >
        <UserLocationMarker />
      </Common.YMapMarker>
    );
  }, [Common, userLocation]);

  // ВСЕГДА рендерим маркеры, если есть данные и Common загружен
  // Никаких проверок на isVisible - маркеры должны быть ВСЕГДА в DOM
  if (!Common) {
    return <div className={styles.container} />;
  }

  return (
    <div 
      className={`${styles.container}${!shouldBeVisible ? ` ${styles.containerHidden}` : ""}`}
    >
      <YandexMap location={location} onMapReady={handleMapReady} common={Common}>
        {markers}
        {userMarker}
      </YandexMap>
    </div>
  );
}

